// Components/Pages/ImpactStories.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaUser, FaCalendar, FaArrowRight, FaImage, FaMapMarkerAlt } from "react-icons/fa";

const ImpactStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedStory, setExpandedStory] = useState(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/stories');
      if (!response.ok) {
        throw new Error('Failed to fetch stories');
      }
      const data = await response.json();
      setStories(data);
    } catch (error) {
      console.error('Error fetching stories:', error);
      setError('Failed to load stories. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const toggleReadMore = (id) => {
    if (expandedStory === id) {
      setExpandedStory(null);
    } else {
      setExpandedStory(id);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading stories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 max-w-md mx-auto">
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={fetchStories}
            className="mt-4 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primaryBlue mb-4">
            Impact Stories
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Real stories of transformation from the communities we serve. These are the faces and voices behind our work.
          </p>
          <div className="w-24 h-1 bg-primaryGold mx-auto mt-4"></div>
        </div>

        {/* Stories Grid */}
        {stories.length === 0 ? (
          <div className="text-center py-12">
            <FaUser className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No stories available yet.</p>
            <p className="text-gray-400 text-sm mt-2">Check back soon for inspiring stories.</p>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto space-y-8">
            {stories.map((story) => {
              const isExpanded = expandedStory === story.id;
              const content = story.full_story || story.summary || '';
              const shouldTruncate = content.length > 250;
              const displayContent = isExpanded ? content : content.substring(0, 250) + (shouldTruncate ? '...' : '');

              return (
                <div 
                  key={story.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Left Side - Image - FIXED HEIGHT */}
                    <div className="md:w-1/3 lg:w-1/4 flex-shrink-0 md:self-stretch">
                      <div className="h-64 md:h-full w-full bg-gray-200" style={{ minHeight: '300px', maxHeight: '400px' }}>
                        {story.image ? (
                          <img 
                            src={`https://api.rigokenya.org${story.image}`}
                            alt={story.author || 'Story image'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x500/1a365d/ffffff?text=Story';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200">
                            <FaImage className="text-6xl" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Side - Content */}
                    <div className="flex-1 p-6 md:p-8 overflow-hidden">
                      {/* Category and Date */}
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          {story.category && (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-primaryGold/10 text-primaryGold">
                              {story.category}
                            </span>
                          )}
                        </div>
                        {story.date && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <FaCalendar className="text-primaryGold" />
                            {story.date}
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h2 className="text-2xl font-bold text-primaryBlue mb-2">
                        {story.title}
                      </h2>

                      {/* Author */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-primaryGold/20 flex items-center justify-center text-primaryGold border-2 border-primaryGold flex-shrink-0">
                          <FaUser className="text-sm" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{story.author || 'Anonymous'}</p>
                          {story.location && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <FaMapMarkerAlt className="text-primaryGold text-[10px]" />
                              {story.location}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Story Content - With max height for consistency */}
                      <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-primaryGold mb-4">
                        <div className="text-gray-700 leading-relaxed whitespace-pre-line text-justify max-h-[300px] overflow-y-auto">
                          {displayContent}
                        </div>
                      </div>

                      {/* Read More Button */}
                      {shouldTruncate && (
                        <button 
                          onClick={() => toggleReadMore(story.id)}
                          className="text-primaryGold hover:text-yellow-600 font-medium flex items-center gap-1 transition-colors duration-300 text-sm"
                        >
                          {isExpanded ? 'Show Less' : 'Read Full Story'} 
                          <FaArrowRight className={`text-xs transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">Have a story to share or want to support our work?</p>
          <Link
            to="/contact"
            className="inline-block bg-primaryGold text-primaryBlue px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Share Your Story
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ImpactStories;